import dynamic from 'next/dynamic';

import { format } from 'date-fns';
import { useIsClient } from 'usehooks-ts';

import { AppValidation } from '@api';

import { MIXPANEL_EVENT_NAMES } from '@shared/api/mixPanel';
import NextLink from '@shared/common/components/NextLink';
import { useAuth } from '@shared/common/providers/AuthProvider';
import { useMixPanel } from '@shared/common/providers/MixPanelProvider';

import AppValidationVotersInfiniteList from '@store:web/components/common/AppValidationVotersInfiniteList/AppValidationVotersInfiniteList';

import { AppPageSectionProps } from './AppPage';
import AppValidationVotingPreload from './AppValidationVotingPreload';

const AppValidationVoting = dynamic(
  () => import('@store:web/components/common/AppValidationVoting/AppValidationVoting'),
  { ssr: false, loading: () => <AppValidationVotingPreload shouldHaveButton={true} /> },
);

type AddValidationProcessSectionProps = AppPageSectionProps & {
  validation: AppValidation;
};

const AddValidationProcessSection = ({ app, validation }: AddValidationProcessSectionProps) => {
  const { user } = useAuth();
  const { track } = useMixPanel();

  const handleTrackedLinkClick = () => {
    const application = app.attributes.appName;

    if (user?.id) {
      track(MIXPANEL_EVENT_NAMES.VALIDATION_APP_PAGE_CLICK, {
        distinct_id: user.id,
        application,
      });
    }
  };

  const {
    attributes: { appLink: appId },
  } = app;

  const isClient = useIsClient();

  return (
    <section className="sm:app-page-section-card flex flex-col">
      <header className="flex justify-between items-baseline mb-8" data-qa="validation-block">
        <h4 className="t-section-title" data-qa="app-validation-title">
          {app.attributes.appName} Magic Store Validation Score
        </h4>

        <NextLink
          href={`/app/${appId}/validation`}
          className="t-body2 text-primary-700 whitespace-nowrap"
          data-qa="validation-results-link"
          onClick={handleTrackedLinkClick}
        >
          See all ››
        </NextLink>
      </header>

      <div className="flex justify-between gap-10 2xl:gap-20 3xl:gap-40 max-2xl:flex-col">
        <div className="w-full lg:pt-9">
          <div className="flex mb-6 lg:mb-12 max-lg:flex-col justify-between gap-4 t-h4">
            <p className="flex flex-col gap-1">
              <span className="text-neutral-500">Start Date:</span>
              {isClient ? (
                <time dateTime={validation.startDate} data-qa="validation-start-date">
                  {format(new Date(validation.startDate), 'MMM d y p')}
                </time>
              ) : (
                <span className="skeleton-text w-[170px]" />
              )}
            </p>

            <p className="flex flex-col gap-1">
              <span className="text-neutral-500">End Date:</span>
              {isClient ? (
                <time dateTime={validation.endDate} data-qa="validation-end-date">
                  {format(new Date(validation.endDate), 'MMM d y p')}
                </time>
              ) : (
                <span className="skeleton-text w-[170px]" />
              )}
            </p>
          </div>

          <AppValidationVoting app={app} validation={validation} />
        </div>

        <div className="flex flex-col w-full p-5 max-sm:border max-sm:border-primary-200 max-sm:rounded-lg sm:p-0">
          <div className="t-h4 mb-4 flex justify-between items-center">
            <span className="text-primary" data-qa="validation-vote-number">
              {validation?.count || 0} Votes
            </span>
          </div>

          <div className="flex-1 min-h-[190px] basis-0 overflow-y-scroll pb-5 px-5 -mx-5 -mb-5 sm:px-8 sm:-mx-8 sm:-mb-8">
            <AppValidationVotersInfiniteList
              className="divide-y divide-primary-100 min-h-full"
              appId={appId}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default AddValidationProcessSection;
